import React from 'react';
import { Breakpoints, Carousel } from '@/components/ui/Carousel/Carousel';
import Image from 'next/image';

enum ImageCarouselThemes {
  LIGHT = 'light',
  DARK = 'dark',
}

interface CarouselItem {
  image: string;
  title?: string;
  subtitle?: string;
  url?: string;
  openInNewWindow?: boolean;
}

export const ImageCarousel: React.FC<{
  theme: ImageCarouselThemes;
  heading: string;
  items: CarouselItem[];
  round: boolean;
}> = ({ theme = ImageCarouselThemes.LIGHT, heading, items, round = false }) => {
  const isDark = theme === ImageCarouselThemes.DARK;

  return (
    <div className={`${isDark ? 'bg-secondary-200 text-white' : ''}`}>
      <h2 className={`${isDark ? 'text-neutral' : ''} mt-0 text-[27px] md:max-w-2/3 md:text-[35px]`}>{heading}</h2>

      <Carousel
        settings={{
          slidesToShow: 5,
          slidesToScroll: 1,
          responsive: [
            {
              breakpoint: Breakpoints.xl,
              settings: {
                slidesToShow: 5,
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: Breakpoints.md,
              settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: Breakpoints.sm,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
              },
            },
          ],
        }}
      >
        {items.map((item, index) => {
          const content = (
            <div key={`image_carousel-${index}`}>
              <div className="flex flex-col items-center text-center">
                <div className={`${round ? 'h-24 w-24' : 'h-40 w-full'} relative`}>
                  <Image
                    fill
                    src={item.image}
                    alt={item.title || ''}
                    sizes={'(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw'}
                    className={`object-contain ${round ? 'rounded-full' : ''}`}
                    quality={60}
                  />
                </div>

                {item.title && (
                  <h3 className={`tc-heading-xs mb-0 md:min-h-[2.4em] ${isDark ? 'text-neutral' : ''}`}>
                    {item.title}
                  </h3>
                )}
                {item.subtitle && (
                  <div
                    dangerouslySetInnerHTML={{ __html: item.subtitle }}
                    className={`text-sm ${isDark ? 'text-neutral' : 'text-dark'}`}
                  />
                )}
              </div>
            </div>
          );

          return item.url ? (
            <a key={index} href={item.url} className="block" target={item.openInNewWindow ? '_blank' : '_self'}>
              {content}
            </a>
          ) : (
            <div key={index}>{content}</div>
          );
        })}
      </Carousel>
    </div>
  );
};
